import { ScrollView, StyleSheet, View } from "react-native";

import { signInMethods } from "@/session/signInMethods";
import type { SignInMethod } from "@/session/types";
import { makeStyles } from "@/theme/makeStyles";
import { Text } from "@/ui/Text";

import { ProductHeader } from "./header/ProductHeader";
import { useScreenStyles } from "./screenStyles";

type Props = {
  /** Defaults to the app's list (empty in 001; 002 adds email + password). */
  methods?: readonly SignInMethod[];
};

/** What a parent does, in the order they do it (PRD §5.0, the parent journey). */
const STEPS = [
  "Scan your school's QR code, or enter its school code",
  "Add your child: the school and class come from the code",
  "Order the booklist your school set, delivered to your door",
] as const;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: {
      flexGrow: 1,
      alignItems: "center",
      paddingHorizontal: t.space.lg,
      paddingVertical: t.space.x3l,
    },
    column: { width: "100%", maxWidth: 440, gap: t.space.x2l },
    intro: { gap: t.space.sm },
    // The journey on a blue tile: the one colour moment on the signed-out entry.
    steps: {
      gap: t.space.md,
      padding: t.space.lg,
      borderRadius: t.radius.card,
      backgroundColor: t.color.tileBlue,
    },
    step: { flexDirection: "row", alignItems: "center", gap: t.space.md },
    number: {
      width: 28,
      height: 28,
      borderRadius: t.radius.card,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: t.color.onTile,
    },
    numeral: { color: t.color.tileBlue },
    stepText: { flex: 1, color: t.color.onTile },
    methods: { gap: t.space.md, width: "100%" },
  }),
);

/**
 * Signed-out entry (FR-002, FR-011; P-AUTH-4; US1-1): the product header, what the app does and how
 * a parent starts, then each sign-in method in order, or "Sign-in is coming soon" while there are none.
 */
export function EntryScreen({ methods = signInMethods }: Props) {
  const screenStyles = useScreenStyles();
  const styles = useStyles();
  return (
    <View style={screenStyles.screen} testID="entry-screen">
      <ProductHeader />
      <ScrollView contentContainerStyle={styles.scroll} role="main">
        <View style={styles.column}>
          <View style={styles.intro}>
            <Text
              variant="display"
              size="x6l"
              accessibilityRole="header"
              aria-level={1}
            >
              {"Welcome to Akaiva Edu\u00A0Power"}
            </Text>
            <Text size="lg" tone="muted">
              School books and stationery for your child, from your
              school&apos;s own booklist.
            </Text>
          </View>
          <View style={styles.steps} role="list">
            {STEPS.map((step, i) => (
              <View key={step} style={styles.step} role="listitem">
                <View style={styles.number} aria-hidden>
                  <Text weight="bold" size="sm" style={styles.numeral}>
                    {String(i + 1)}
                  </Text>
                </View>
                <Text style={styles.stepText}>{step}</Text>
              </View>
            ))}
          </View>
          {methods.length === 0 ? (
            <Text tone="muted" style={screenStyles.message}>
              Sign-in is coming soon
            </Text>
          ) : (
            <View style={styles.methods}>
              {methods.map(({ id, EntryComponent }) => (
                <EntryComponent key={id} />
              ))}
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}
